// Phase 1a — Shared asset domain types.
// All API timestamp fields are ISO strings (parseDate / parseTimestamp in date-utils.js handles rendering).

export type AssetCategory = 'dynamic' | 'stable' | 'kit';
// 'building' is derived from SmartCharger/TimeMachine build_phase != in_service.
export type OperationalStatus = 'ready' | 'building' | 'flagged' | 'ooc' | 'checked_out' | 'retired';
export type InventoryCategory = 'raw' | 'sub_assembly' | 'wip' | 'finished_good' | 'cogs' | 'retired';
// 'At Customer' is reserved for the future scanning system; the assets_with_location
// view never produces it today (customer assignment is a business state, not a location).
export type LocationBucket = 'Headquarters' | 'At Event' | 'At Tour' | 'At Customer';
export type LocationType = 'warehouse' | 'event' | 'tour' | 'customer' | 'unknown' | 'flashed' | 'legacy';

// Ownership is DERIVED server-side from two orthogonal spine facts — is_internal_pool
// (Firefly owns it) and current_customer_id (who holds it) — with the generated is_sold
// winning outright. It arrives as `ownership` on every asset row; never recompute it in
// a component.
//
// There are FOUR values and the fourth is null. Null is a real state, not a loading gap:
// rows onboarded as "purchased" with no sale record behind them wait there for a human
// audit. Render null as absence (the em-dash placeholder), never as an error, and never
// fold it into one of the three.
export type AssetOwnership = 'internal' | 'lease' | 'sold';

export const OWNERSHIP_LABELS: Record<AssetOwnership, string> = {
  internal: 'Internal',
  lease:    'Lease',
  sold:     'Sold',
};

// Sort key for the Ownership column: lifecycle order, with the unclassified rows as one
// block at the end. `undefined` for null is the shared table's contract for an empty —
// it is what groups the audit worklist together instead of scattering it.
export const OWNERSHIP_SORT_ORDER: Record<AssetOwnership, number> = {
  internal: 0,
  lease:    1,
  sold:     2,
};
export const ownershipSortValue = (o: AssetOwnership | null | undefined): number | undefined =>
  o ? OWNERSHIP_SORT_ORDER[o] : undefined;

// TimeMachine passes through these imaging phases before becoming ready.
// Driven by the operational_status trigger: anything != in_service → building.
export type BuildPhase = 'up_next' | 'sd_card_ready' | 'default_image' | 'working' | 'in_service';

// SmartCharger uses the shop's 8-step PHYSICAL build pipeline (forked off the shared
// enum — see firefleet-api charger-build-phase-pipeline.sql). Terminal phase =
// build_complete; "in service" is now a Status concept, not a build phase.
export type ChargerBuildPhase =
  | 'in_queue' | 'sub_assembly_prep' | 'phase_1' | 'phase_2' | 'phase_3'
  | 'bench_testing' | 'systems' | 'build_complete';

export interface AssetType {
  id: number;
  key: string;
  label: string;
  category: AssetCategory;
  barcode_prefix: string | null;
  sequence_next: number;
  operational_statuses: string[];
  requires_extension_table: boolean;
  extension_table_name: string | null;
  custom_fields_schema: Record<string, unknown> | null;
  is_built_in_house: boolean;
  auto_issue_display_code: boolean;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
}

export interface Location {
  id: number;
  name: string;
  type: LocationType;
  parent_location_id: number | null;
  address: string | null;
  is_active: boolean;
  /** Stock role: shelf stock, received into and picked from. */
  is_inventory: boolean;
  /** Stock role: the run pool. At most one active. */
  is_assembly: boolean;
  created_at: string;
}

// Extension-table rows (one shape per type.extension_table_name).
export interface SmartChargerDetails {
  asset_id: string;
  sequence_number: number | null;
  vpn_ip: string | null;
  vpn_confirmed: boolean;
  firmware_version: string | null;
  hardware_rev: string | null;
  last_telemetry_at: string | null;
  last_seen_geo: string | null;    // durable last-known city (persists when offline)
  last_seen_lat: number | null;
  last_seen_lng: number | null;
  build_phase: ChargerBuildPhase;
  // True when this charger is running the currently-logged firmware release.
  // Flipped to false for every charger when an admin presses "Log Firmware Update";
  // flipped back to true per-charger as operators update each one in the shop.
  on_latest_firmware: boolean;
  charger_power: 'regular' | 'super' | null;
}
export interface TimeMachineDetails {
  asset_id: string;
  sequence_number: number | null;
  vpn_ip: string | null;
  vpn_confirmed: boolean;
  firmware_version: string | null;
  build_phase: BuildPhase;
  // Same semantics as SmartChargerDetails.on_latest_firmware — flipped per-unit as
  // operators apply the latest firmware build during shop rollouts.
  on_latest_firmware: boolean;
}
export interface RtkUnitDetails {
  asset_id: string;
  firmware_version: string | null;
  // See TimeMachineDetails.on_latest_firmware.
  on_latest_firmware: boolean;
}
export interface GcsDetails {
  asset_id: string;
  vpn_ip: string | null;
  last_telemetry_at: string | null;
  notes: string | null;
}
export type AssetExtension =
  | SmartChargerDetails
  | TimeMachineDetails
  | RtkUnitDetails
  | GcsDetails;

// Main spine row, as served by GET /api/assets and /api/assets/:id (via assets_with_location view).
export interface Asset {
  id: string;
  asset_type_id: number;
  display_code: string;
  barcode: string | null;
  operational_status: OperationalStatus;
  inventory_category: InventoryCategory | null;
  cost_basis: string | null;
  date_completed: string | null;
  date_shipped: string | null;
  data_quality: string | null;
  current_event_id: string | null;
  current_tour_id: string | null;
  current_customer_id: number | null;
  parent_asset_id: string | null;
  custom_fields: Record<string, unknown>;
  notes: string | null;
  is_active: boolean;
  sold_to_customer_id: number | null;
  sold_at: string | null;
  is_sold: boolean;
  is_internal_pool: boolean;
  created_at: string;
  created_by: string | null;
  location_bucket: LocationBucket;
  // Derived, not stored — see AssetOwnership. Served by the list, the detail read and
  // every create/update response, so a row merged back after an inline edit stays honest.
  ownership: AssetOwnership | null;

  // Present on GET /api/assets/:id (hydrated response):
  asset_type?: AssetType;
  extension?: AssetExtension | null;
  movement_log?: AssetMovementLogRow[];
  barcode_history?: AssetBarcodeHistoryRow[];
  children?: ChildAssetSummary[];
}

// Children returned on the hydrated asset response — joined with asset_types for label rendering.
export interface ChildAssetSummary extends Asset {
  asset_type_key: string;
  asset_type_label: string;
  asset_type_category: AssetCategory;
}

export interface AssetMovementLogRow {
  id: string;
  asset_id: string;
  from_location_id: number | null;
  to_location_id: number;
  event_id: string | null;
  performed_by: string | null;
  occurred_at: string;
  note: string | null;
}

export interface AssetBarcodeHistoryRow {
  id: string;
  asset_id: string;
  old_barcode: string | null;
  new_barcode: string;
  changed_by: string | null;
  changed_at: string;
  reason: string | null;
}

export interface AssetFlag {
  id: number;
  asset_id: string;
  descript: string;
  statusdesc: 'Flag' | 'OOC';
  user_id: string | null;
  dateflagged: string;
  active: boolean;
}

// Merged-timeline entry from GET /api/assets/:id/history. Defined in
// components/EntityHistory/types, with the component that renders it: the
// timeline is not an asset concept (drones migrate onto it next), and a shared
// component importing this folder to name its own props was the layering
// upside down. Re-exported here for the Assets-side consumers that read it here.
export type { HistoryEntry, HistoryEntryType } from '../components/EntityHistory/types';

export interface AssetsListResponse {
  data: Asset[];
  page: number;
  pageSize: number;
  total: number;
}

// One row returned by GET /api/assets/by-type-counts. Feeds the /assets landing grid.
export interface AssetTypeCount {
  id: number;
  key: string;
  label: string;
  category: AssetCategory;
  total: number;
  ready: number;
  building: number;
  flagged: number;
  ooc: number;
  checked_out: number;
  retired: number;
  needs_update: number;
  // The ownership breakdown. These four PARTITION the type's rows, so
  // internal + lease + sold + unclassified === total, always. `unclassified` is
  // counted rather than inferred by subtraction: it is the audit worklist, and a card
  // that showed only the other three would quietly drop it.
  internal: number;
  lease: number;
  sold: number;
  unclassified: number;
}

// Preview payload from GET /api/assets/preview-name?type_key=...
export interface AssetNamePreview {
  sequence_number: number;
  display_code: string;
  vpn_ip: string | null;
}

// Filter state for AssetsPage.
export interface AssetsFilterState {
  q: string;
  asset_type_id: number | null;
  location_bucket: LocationBucket | 'all' | null;
  operational_status: OperationalStatus | 'all' | null;
  is_internal_pool: 'true' | 'false' | 'all';
  is_sold: 'true' | 'false' | 'all';
  page: number;
  pageSize: number;
}

export const DEFAULT_FILTERS: AssetsFilterState = {
  q: '',
  asset_type_id: null,
  location_bucket: null,
  operational_status: null,
  is_internal_pool: 'true',
  is_sold: 'false',
  page: 1,
  pageSize: 50,
};

export const STATUS_LABELS: Record<OperationalStatus, string> = {
  ready:       'Ready',
  building:    'Building',
  flagged:     'Flagged',
  ooc:         'Out of Commission',
  checked_out: 'Checked Out',
  retired:     'Retired',
};

// ---- Derived charger status (Manage table) ---------------------------------
// A read-only lifecycle label computed from existing signals (operational_status
// + ownership + commit FKs) — NOT a stored field, so it can't drift from reality.
// Reserved-for-Show and Returning-to-Systems are deferred, so all in-scope states
// derive purely from data already on the asset row (no backend join).
export type ChargerStatus =
  | 'assembly' | 'needs_repair' | 'purchased' | 'leased' | 'home_base' | 'traveling' | 'retired';

export function chargerStatus(a: {
  operational_status: OperationalStatus;
  is_internal_pool: boolean;
  current_customer_id: number | null;
  current_event_id: string | null;
  current_tour_id: string | null;
}): ChargerStatus {
  switch (a.operational_status) {
    case 'retired':  return 'retired';
    case 'ooc':
    case 'flagged':  return 'needs_repair';
    case 'building': return 'assembly';
    case 'checked_out':
      // Committed somewhere: event/tour = out on a show; else at a customer, where
      // ownership tells purchased (sold) vs leased (Firefly-owned, customer holds it).
      if (a.current_event_id || a.current_tour_id) return 'traveling';
      if (a.current_customer_id != null) return a.is_internal_pool ? 'leased' : 'purchased';
      return a.is_internal_pool ? 'home_base' : 'purchased';
    case 'ready':
    default:
      // Built + uncommitted: internal ⇒ in the shop (Home Base); sold-but-idle ⇒ purchased.
      return a.is_internal_pool ? 'home_base' : 'purchased';
  }
}

export const CHARGER_STATUS_LABELS: Record<ChargerStatus, string> = {
  assembly:     'Assembly',
  needs_repair: 'Needs Repair',
  purchased:    'In service: Purchased',
  leased:       'In service: Leased',
  home_base:    'Home Base',
  traveling:    'Traveling',
  retired:      'Retired',
};

export const CATEGORY_LABELS: Record<AssetCategory, string> = {
  dynamic: 'Dynamic',
  stable:  'Stable',
  kit:     'Kit',
};

// Build-phase labels for SmartCharger/TimeMachine extension editing. Order matters —
// displayed in dropdowns in shop-floor progression order.
export const BUILD_PHASE_LABELS: Record<BuildPhase, string> = {
  up_next:        'Up Next',
  sd_card_ready:  'SD Card Ready',
  default_image:  'Default Image',
  working:        'Working',
  in_service:     'In Service',
};

export const BUILD_PHASE_ORDER: BuildPhase[] = [
  'up_next', 'sd_card_ready', 'default_image', 'working', 'in_service',
];

// SmartCharger physical build pipeline — shop-floor progression order.
export const CHARGER_BUILD_PHASE_LABELS: Record<ChargerBuildPhase, string> = {
  in_queue:          'In Queue',
  sub_assembly_prep: 'Sub-Assembly Prep',
  phase_1:           'Phase 1 · Case Prep & I/O',
  phase_2:           'Phase 2 · Module & Cradle',
  phase_3:           'Phase 3 · Screen & Finishing',
  bench_testing:     'Bench Testing',
  systems:           'Systems · Ready to Ship',
  build_complete:    'Build Complete',
};

export const CHARGER_BUILD_PHASE_ORDER: ChargerBuildPhase[] = [
  'in_queue', 'sub_assembly_prep', 'phase_1', 'phase_2', 'phase_3',
  'bench_testing', 'systems', 'build_complete',
];

export const INVENTORY_CATEGORY_LABELS: Record<InventoryCategory, string> = {
  raw:           'Raw',
  sub_assembly:  'Sub-Assembly',
  wip:           'WIP',
  finished_good: 'Finished Good',
  cogs:          'COGS',
  retired:       'Retired',
};
